// packages/web/src/lib/radarFreshness.ts — R3b-2: ONE freshness-line
// formula, read straight off a run row (pythia_radar_topics' `last_run` on
// the topics list, or a `runs[]` entry from pythia_radar_topic_get) — never
// a calm line over a failed run (a set `error` always names it), and never
// a string composed anywhere else. Matches pythia's own description of the
// line (src/tools/radar.ts, pythia_radar_topics): "seen → clusters →
// triaged → shown".
export interface RadarRunLike {
  kind: string | null;
  startedAt?: string | null;
  finishedAt: string | null;
  sourcesPolled: number | null;
  sourcesFailed?: number | null;
  sourcesStale: number | null;
  staleNames?: string[] | null;
  // R5 (C, 2026-09-28) — sources the per-class cadence (spec Part P3) chose
  // not to poll this run, never counted as failed. Optional so an older
  // caller (or a Pythia build that predates R5) can omit it — the clause
  // simply doesn't appear.
  sourcesSkipped?: number | null;
  seen: number | null;
  clusters: number | null;
  triaged: number | null;
  shown: number | null;
  error: string | null;
}

function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return 'time unknown';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// R5 (C) — "the freshness line names what is stale": the first three names,
// then '+N' for the rest. null when there is nothing to name (no names, or
// none given at all) — the caller then shows the bare count with no
// parenthetical, exactly as before R5.
function namedList(names: string[] | null | undefined, cap = 3): string | null {
  if (!names || names.length === 0) return null;
  const shown = names.slice(0, cap);
  const rest = names.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} +${rest}` : shown.join(', ');
}

/** 'polled <when> · <n> sources, <n> stale (A, B, C +2) · <n> skipped ·
 *  <n> seen → <n> clusters → <n> triaged → <n> shown', or '<kind> failed —
 *  <error>' when the run errored, or '<kind> in progress' while it is
 *  still running, or 'no runs yet' when there is none. The stale
 *  parenthetical names `staleNames` (R5 design rule 5 — "the freshness
 *  line names what is stale"); the skipped clause appears only when
 *  `sourcesSkipped` is set and > 0 — a run that skipped nothing (or a
 *  Pythia build that predates the per-class cadence) reads exactly as
 *  before R5. */
export function freshnessLine(run: RadarRunLike | null | undefined): string {
  if (!run) return 'no runs yet';
  if (run.error) return `${run.kind ?? 'run'} failed — ${run.error}`;
  if (!run.finishedAt) return `${run.kind ?? 'run'} in progress`;
  const parts: string[] = [`polled ${fmtWhen(run.startedAt ?? run.finishedAt)}`];
  if (run.sourcesPolled !== null) {
    const staleList = namedList(run.staleNames);
    parts.push(`${run.sourcesPolled} sources, ${run.sourcesStale ?? 0} stale${staleList ? ` (${staleList})` : ''}`);
  }
  if ((run.sourcesSkipped ?? 0) > 0) parts.push(`${run.sourcesSkipped} skipped`);
  if (run.seen !== null || run.clusters !== null || run.triaged !== null || run.shown !== null) {
    parts.push(`${run.seen ?? 0} seen → ${run.clusters ?? 0} clusters → ${run.triaged ?? 0} triaged → ${run.shown ?? 0} shown`);
  }
  return parts.join(' · ');
}

// A topic's newest run is "researching" while it is queued/running and has
// not finished — R3b-2 item 6's own kind list (research_now/scheduled_run
// from radar_commands, discover/review the run rows those commands open).
// R3b-3 (C1): kept as the FALLBACK for a Pythia build that predates P2 (a
// `pending_command` that is always null then) — the primary read is now
// `pending_command`, below.
const RESEARCHING_KINDS = new Set(['research_now', 'scheduled_run', 'discover', 'review']);

function runLooksResearching(run: RadarRunLike | null | undefined): boolean {
  return !!run && !!run.kind && RESEARCHING_KINDS.has(run.kind) && !run.finishedAt;
}

/** Pythia_radar_topics' `pending_command` shape — the newest queued/running
 *  `radar_commands` row for a topic, or the fields this function actually
 *  reads off it. */
export interface RadarPendingCommandLike {
  status: string;
}

/** R3b-3 (P2/C1): a topic is "researching" the instant its `pending_command`
 *  is queued or running — set the moment a tap lands, not when the runner
 *  eventually opens a run row (its own poll can lag a tap by minutes). Falls
 *  back to the OLD run-kind heuristic only when `pending` is null/undefined
 *  AGAINST a Pythia build that predates P2 (which always answers null for
 *  it) — `run` is optional so a caller with no run object at all (a bare
 *  pending-command check) can omit it. */
export function isResearching(
  pending: RadarPendingCommandLike | null | undefined,
  run?: RadarRunLike | null
): boolean {
  if (pending) return pending.status === 'queued' || pending.status === 'running';
  return runLooksResearching(run);
}

/** R3b-3 (C1): the freshness line's real formula — the "seen -> clusters ->
 *  triaged -> shown" line reads the newest DISCOVER run, never whichever
 *  run happens to be newest (a review's own row carries no source/seen
 *  counts of its own, which is what read as "0 seen -> 0 clusters -> 0
 *  triaged -> 0 shown" after a review, on the first real walk). The
 *  failed-run line still reads `lastRun.error` — a failed run is worth
 *  naming regardless of which leg it was. */
export function topicFreshnessLine(
  lastDiscover: RadarRunLike | null | undefined,
  lastRun: RadarRunLike | null | undefined
): string {
  if (lastRun?.error) return `${lastRun.kind ?? 'run'} failed — ${lastRun.error}`;
  return freshnessLine(lastDiscover);
}
